// frontend/src/App.js
import React, { Suspense, lazy, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import LandingPage from './LandingPage';
import TestModeWrapper from './TestModeWrapper';
import './App.css';
import { trackPageView } from './utils/analytics';
import { isWidgetRoute } from './utils/isWidgetRoute';
import FrameGuard from './components/FrameGuard';
import PrerenderHandoff, { wasPrerendered } from './components/PrerenderHandoff';

// Route-level code splitting: every page except the landing page loads its own
// chunk, so widget code stays out of the main bundle.
const SimpleDashboard = lazy(() => import('./SimpleDashboard'));
const WidgetEmbedPage = lazy(() => import('./WidgetEmbedPage'));
const AuthDebugDetailed = lazy(() => import('./AuthDebugDetailed'));
const LCTCalculatorPage = lazy(() => import('./pages/LCTCalculatorPage'));
const DocsPage = lazy(() => import('./pages/DocsPage'));
const IntegrationGuidePage = lazy(() => import('./pages/IntegrationGuidePage'));
const SignupPage = lazy(() => import('./pages/SignupPage'));
const CalculationsPage = lazy(() => import('./pages/CalculationsPage'));
const MethodologyPage = lazy(() => import('./pages/MethodologyPage'));
const DemoSitePage = lazy(() => import('./pages/DemoSitePage'));
const BoilerEscapePlanPage = lazy(() => import('./pages/BoilerEscapePlanPage'));
const PrinciplesPage = lazy(() => import('./pages/PrinciplesPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const ToolPage = lazy(() => import('./pages/ToolPage'));

// Track page views on route change
function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);
  return null;
}

// Landing-page anchors that became /tools/<slug> pages; keeps old links working
const LEGACY_HASH_ROUTES = {
  '#grant-finder': '/tools/grant-finder',
  '#boiler-escape-plan': '/tools/boiler-escape-plan',
  '#installer-finder': '/tools/installer-finder',
  '#ar-demo': '/tools/heat-pump-visualiser',
  '#sound-experience': '/tools/sound-simulator',
  '#house-tour': '/tools/house-tour',
};

// Scroll to #hash targets after client-side navigation (e.g. /#tools
// from a tool page); scroll to top on plain route changes.
//
// Two things make a single scrollIntoView unreliable here: the target element
// may not be mounted on the first frame, and the landing page keeps growing
// after that frame as demo widgets and images load, which pushes the target
// further down. So we re-scroll whenever the target's document offset moves,
// for a short window, and give up as soon as the reader scrolls themselves.
// `location.key` is in the deps so clicking a link to the hash you are already
// on (same pathname + hash, new history entry) still scrolls.
function ScrollManager() {
  const location = useLocation();
  const navigate = useNavigate();
  // On a pre-rendered page the visitor may already have scrolled the static
  // copy before the app mounts; the first mount must not jump them to the top.
  const firstRun = useRef(true);
  useEffect(() => {
    const isFirstRun = firstRun.current;
    firstRun.current = false;
    if (location.pathname === '/' && LEGACY_HASH_ROUTES[location.hash]) {
      navigate(LEGACY_HASH_ROUTES[location.hash], { replace: true });
      return undefined;
    }
    if (!location.hash) {
      if (!(isFirstRun && wasPrerendered)) window.scrollTo(0, 0);
      return undefined;
    }

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const behavior = reduceMotion ? 'auto' : 'smooth';
    let cancelled = false;
    let lastOffset = null;
    let timer = null;

    const cancel = () => { cancelled = true; };
    window.addEventListener('wheel', cancel, { passive: true });
    window.addEventListener('touchstart', cancel, { passive: true });
    window.addEventListener('keydown', cancel);

    const deadline = Date.now() + 3000;
    const tick = () => {
      if (cancelled) return;
      const el = document.querySelector(location.hash);
      if (el) {
        const offset = Math.round(el.getBoundingClientRect().top + window.scrollY);
        if (offset !== lastOffset) {
          lastOffset = offset;
          el.scrollIntoView({ behavior, block: 'start' });
        }
      }
      if (Date.now() < deadline) timer = setTimeout(tick, 250);
    };
    const frame = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      if (timer) clearTimeout(timer);
      window.removeEventListener('wheel', cancel);
      window.removeEventListener('touchstart', cancel);
      window.removeEventListener('keydown', cancel);
    };
  }, [location.pathname, location.hash, location.key, navigate]);
  return null;
}


function App() {
  // Widget embeds render without AuthProvider. It calls Clerk's useUser() and
  // then hard-gates the entire tree on `isLoaded`, which never becomes true in
  // a cross-site iframe because Clerk cannot reach localStorage there. Nothing
  // in the widget tree consumes auth: AuthContext defaults to {}, the AR widget
  // already destructures with `|| {}` and is passed requireAuth={false}.
  // See utils/isWidgetRoute.js.
  if (isWidgetRoute()) {
    return (
      <TestModeWrapper>
        <Router>
          <PageViewTracker />
          <Suspense fallback={<div className="min-h-screen bg-surface-50" />}>
            <Routes>
              <Route path="/widget-embed" element={<WidgetEmbedPage />} />
              <Route path="*" element={<WidgetEmbedPage />} />
            </Routes>
          </Suspense>
        </Router>
      </TestModeWrapper>
    );
  }

  return (
    <AuthProvider>
      <TestModeWrapper>
        <Router>
          <PageViewTracker />
          <ScrollManager />
          <Suspense fallback={<div className="min-h-screen bg-surface-50" />}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/dashboard" element={<FrameGuard><SimpleDashboard /></FrameGuard>} />
            <Route path="/widget-embed" element={<WidgetEmbedPage />} />
            <Route path="/lct-calculator" element={<LCTCalculatorPage />} />
            <Route path="/configure" element={<FrameGuard><SimpleDashboard /></FrameGuard>} />
            <Route path="/signup" element={<FrameGuard><SignupPage /></FrameGuard>} />
            <Route path="/pricing" element={<LandingPage />} />
            <Route path="/features" element={<LandingPage />} />
            <Route path="/docs" element={<DocsPage />} />
            <Route path="/docs/integration" element={<IntegrationGuidePage />} />
            <Route path="/calculations" element={<CalculationsPage />} />
            <Route path="/methodology" element={<MethodologyPage />} />
            <Route path="/methodology/:slug" element={<MethodologyPage />} />
            <Route path="/demo" element={<DemoSitePage />} />
            <Route path="/tools/:slug" element={<ToolPage />} />
            <Route path="/boiler-escape-plan" element={<BoilerEscapePlanPage />} />
            <Route path="/principles" element={<PrinciplesPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/debug-auth-detailed" element={<AuthDebugDetailed />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <PrerenderHandoff />
          </Suspense>
        </Router>
      </TestModeWrapper>
    </AuthProvider>
  );
}

export default App;